import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import CustomEditor from "../common/Editor";
import { Plus, Trash2 } from "lucide-react";
import ExplanationField from "./ExplanationField";

function splitPassage(str = "") {
  // تقسیم با نقطه/علامت پرسش/تعجب/نیم‌فاصله‌های متداول + خط جدید
  if (!str.trim()) return [];
  const parts = str
    .split(/(?<=[\.\!\?؟؛])\s+|\n+/g)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts;
}

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  passage_fa: "",
  passage_en: "",
  sentences: [],
  selectionMode: "single",
  layout: "top-bottom",
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};

  const passage_fa = body?.passage?.fa || "";
  const passage_en = body?.passage?.en || "";

  const selectionMode = body.selectionMode || "single";
  const layout = body.layout || "top-bottom";

  const sentencesBody = Array.isArray(body.sentences) ? body.sentences : [];
  const correctIds = Array.isArray(body.correctIds) ? body.correctIds : [];

  const sentences = sentencesBody.map((s, idx) => {
    const id = s.id || `S${idx + 1}`;
    return {
      id,
      fa: s.text?.fa || "",
      en: s.text?.en || "",
      correct: correctIds.includes(id),
    };
  });

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  return {
    stem_fa,
    stem_en,
    passage_fa,
    passage_en,
    sentences,
    selectionMode,
    layout,
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function SelectSentenceModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  const correctCount = useMemo(
    () => form.sentences.filter((s) => s.correct).length,
    [form.sentences]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const regenerateFromPassage = () => {
    const faParts = splitPassage(form.passage_fa);
    const enParts = splitPassage(form.passage_en);
    const maxLen = Math.max(faParts.length, enParts.length);
    const next = Array.from({ length: maxLen }).map((_, i) => ({
      id: `S${i + 1}`,
      fa: faParts[i] || "",
      en: enParts[i] || "",
      correct: false,
    }));
    setForm((f) => ({ ...f, sentences: next }));
  };

  const addSentence = () =>
    setForm((f) => ({
      ...f,
      sentences: [
        ...f.sentences,
        { id: `S${f.sentences.length + 1}`, fa: "", en: "", correct: false },
      ],
    }));

  const removeSentence = (idx) =>
    setForm((f) => ({
      ...f,
      sentences: f.sentences.filter((_, i) => i !== idx),
    }));

  const submit = async (e) => {
    e.preventDefault();

    if (!form.stem_fa.trim() && !form.stem_en.trim()) {
      toast.error(t("select_sentence.toasts.stem_required"));
      return;
    }
    if (!form.passage_fa.trim() && !form.passage_en.trim()) {
      toast.error(t("select_sentence.toasts.passage_required"));
      return;
    }
    if (form.sentences.length < 1) {
      toast.error(t("select_sentence.toasts.need_sentences"));
      return;
    }
    if (correctCount === 0) {
      toast.error(t("select_sentence.toasts.need_correct"));
      return;
    }
    if (form.selectionMode === "single" && correctCount > 1) {
      toast.error(t("select_sentence.toasts.only_one_allowed"));
      return;
    }

    const correctIds = form.sentences.filter((s) => s.correct).map((s) => s.id);

    const payload = {
      kind: "select_sentence",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: { fa: form.stem_fa || undefined, en: form.stem_en || undefined },
      body: {
        passage: {
          fa: form.passage_fa || undefined,
          en: form.passage_en || undefined,
        },
        sentences: form.sentences.map((s) => ({
          id: s.id,
          text: { fa: s.fa || undefined, en: s.en || undefined },
        })),
        correctIds,
        selectionMode: form.selectionMode,
        layout: form.layout,
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("select_sentence.toasts.updated", "آیتم با موفقیت ویرایش شد")
          : t("select_sentence.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Stem */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div dir={isRtl ? "rtl" : "ltr"}>
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.stem_fa")}
          </label>
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("select_sentence.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={100}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
        <div dir="ltr">
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.stem_en")}
          </label>
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("select_sentence.stem_en_ph")}
            directionality="ltr"
            height={100}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />
        </div>
      </div>
      {/* Passage */}
      <div>
        {" "}
        <label className="block text-sm font-medium mb-1">
          {t("select_sentence.passage")}{" "}
        </label>{" "}
        <textarea
          className="w-full border rounded-lg px-3 py-2"
          rows={5}
          placeholder={t("select_sentence.passage_fa_ph")}
          value={form.passage_fa}
          onChange={(e) =>
            setForm((f) => ({ ...f, passage_fa: e.target.value }))
          }
        />{" "}
        <textarea
          className="w-full border rounded-lg px-3 py-2 mt-2"
          rows={4}
          placeholder={t("select_sentence.passage_en_ph")}
          value={form.passage_en}
          onChange={(e) =>
            setForm((f) => ({ ...f, passage_en: e.target.value }))
          }
        />{" "}
        <div className="mt-2">
          {" "}
          <button
            type="button"
            className="px-3 py-1 rounded border text-sm"
            onClick={regenerateFromPassage}
          >
            {t("select_sentence.auto_split")}{" "}
          </button>{" "}
        </div>{" "}
      </div>
      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("select_sentence.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`select_sentence.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>
      {/* Sentences list */}{" "}
      <div>
        {" "}
        <div className="flex items-center justify-between mb-1">
          {" "}
          <label className="block text-sm font-medium">
            {t("select_sentence.sentences")}{" "}
          </label>{" "}
          <button
            type="button"
            className="text-blue-600 text-sm inline-flex items-center gap-1"
            onClick={addSentence}
          >
            <Plus size={16} /> {t("select_sentence.add_sentence")}{" "}
          </button>{" "}
        </div>{" "}
        <div className="space-y-2">
          {" "}
          {form.sentences.map((s, idx) => (
            <div key={s.id} className="border rounded-lg p-3 space-y-2">
              {" "}
              <div className="flex items-center justify-between">
                {" "}
                <div className="text-xs font-semibold">{s.id}</div>{" "}
                <div className="flex items-center gap-2">
                  {" "}
                  <label className="text-xs">
                    {t("select_sentence.correct")}{" "}
                  </label>{" "}
                  <input
                    type={
                      form.selectionMode === "single" ? "radio" : "checkbox"
                    }
                    name="correctSentence"
                    checked={!!s.correct}
                    onChange={(e) => {
                      if (form.selectionMode === "single") {
                        setForm((f) => ({
                          ...f,
                          sentences: f.sentences.map((x) => ({
                            ...x,
                            correct: x.id === s.id,
                          })),
                        }));
                      } else {
                        const sentences = [...form.sentences];
                        sentences[idx].correct = e.target.checked;
                        setForm((f) => ({ ...f, sentences }));
                      }
                    }}
                  />{" "}
                  {form.sentences.length > 1 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removeSentence(idx)}
                    >
                      <Trash2 size={16} />{" "}
                    </button>
                  )}{" "}
                </div>{" "}
              </div>{" "}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {" "}
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("select_sentence.sentence_fa_ph")}
                  value={s.fa}
                  onChange={(e) => {
                    const sentences = [...form.sentences];
                    sentences[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, sentences }));
                  }}
                />{" "}
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder={t("select_sentence.sentence_en_ph")}
                  value={s.en}
                  onChange={(e) => {
                    const sentences = [...form.sentences];
                    sentences[idx].en = e.target.value;
                    setForm((f) => ({ ...f, sentences }));
                  }}
                />{" "}
              </div>{" "}
            </div>
          ))}{" "}
        </div>{" "}
      </div>
      {/* Meta */}{" "}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.selection_mode")}{" "}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.selectionMode}
            onChange={(e) =>
              setForm((f) => ({ ...f, selectionMode: e.target.value }))
            }
          >
            {" "}
            <option value="single">
              {t("select_sentence.mode_single")}
            </option>{" "}
            <option value="multiple">
              {t("select_sentence.mode_multiple")}{" "}
            </option>{" "}
          </select>{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.points")}{" "}
          </label>{" "}
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.status")}{" "}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            {" "}
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>{" "}
          </select>{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("select_sentence.tags")}{" "}
          </label>{" "}
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />{" "}
        </div>{" "}
      </div>{" "}
      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>{" "}
    </form>
  );
}
